import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Plus } from 'lucide-react';
import { useNotify } from 'react-admin';
import { Tag, logger } from '@musedrops/shared';
import { TagApi } from '../../../services/tagApi';

interface TagWithUsage extends Tag {
  promptKeys: string[]; // List of prompt template keys that use this tag
}

export const TagsSettings: React.FC = () => {
  const notify = useNotify();
  const [tags, setTags] = useState<TagWithUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state - shared for create and edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingTag, setEditingTag] = useState<TagWithUsage | null>(null);
  const [dialogValue, setDialogValue] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState<TagWithUsage | null>(null);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);

      const [allTags, promptTagsMap] = await Promise.all([
        TagApi.getAll(),
        TagApi.getTagsUsedInPromptsWithKeys(),
      ]);

      // Map tags to include which prompt templates use them
      const tagsWithUsage = allTags.map(tag => ({
        ...tag,
        promptKeys: promptTagsMap.get(tag.tag) || [],
      }));

      setTags(tagsWithUsage);
    } catch (err) {
      logger.error('Failed to load tags:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setDialogMode('create');
    setEditingTag(null);
    setDialogValue('');
    setDialogOpen(true);
  };

  const handleEditClick = (tag: TagWithUsage) => {
    setDialogMode('edit');
    setEditingTag(tag);
    setDialogValue(tag.tag);
    setDialogOpen(true);
  };

  const handleDialogSave = async () => {
    if (dialogMode === 'create') {
      try {
        await TagApi.create(dialogValue);
        notify('Tag created successfully', { type: 'success' });
        setDialogOpen(false);
        setDialogValue('');
        loadTags(); // Reload to get updated data
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Failed to create tag', {
          type: 'error',
        });
      }
    } else {
      if (!editingTag) return;

      try {
        await TagApi.update(editingTag.id, dialogValue);
        notify('Tag renamed successfully', { type: 'success' });
        setDialogOpen(false);
        setEditingTag(null);
        setDialogValue('');
        loadTags(); // Reload to get updated data
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Failed to rename tag', {
          type: 'error',
        });
      }
    }
  };

  const handleDeleteClick = (tag: TagWithUsage) => {
    setDeletingTag(tag);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTag) return;

    try {
      await TagApi.delete(deletingTag.id);
      notify('Tag deleted successfully', { type: 'success' });
      setDeleteDialogOpen(false);
      setDeletingTag(null);
      loadTags(); // Reload to get updated data
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to delete tag', {
        type: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tag Name</TableCell>
              <TableCell>Used in Prompts</TableCell>
              <TableCell align="center">
                <IconButton
                  onClick={handleCreateClick}
                  color="primary"
                  size="small"
                >
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No tags yet. Create tags by adding them to shows or stories.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tags.map(tag => (
                <TableRow key={tag.id}>
                  <TableCell>
                    <code>{tag.tag}</code>
                  </TableCell>
                  <TableCell>
                    {tag.promptKeys.length > 0 ? (
                      <Typography variant="body2">
                        {tag.promptKeys.join(', ')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(tag)}
                      disabled={tag.promptKeys.length > 0}
                      title={
                        tag.promptKeys.length > 0
                          ? 'Cannot rename - used in prompt templates'
                          : 'Rename tag'
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(tag)}
                      disabled={tag.promptKeys.length > 0}
                      title={
                        tag.promptKeys.length > 0
                          ? 'Cannot delete - used in prompt templates'
                          : 'Delete tag'
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        {dialogMode === 'edit' && <DialogTitle>Rename Tag</DialogTitle>}
        {dialogMode === 'create' && <DialogTitle>Create Tag</DialogTitle>}
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tag Name"
            fullWidth
            value={dialogValue}
            onChange={e =>
              setDialogValue(TagApi.normalizeTagName(e.target.value))
            }
            helperText="Will be auto-converted to kebab-case"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tag{' '}
            <code>{deletingTag?.tag}</code>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will remove it from all shows and stories that use it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
